import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from '../static/styles/WeeklyTournaments.module.css';
import calendarIcon from '../static/img/Home/calendarIcon.svg';
import { fetchWeeklyTournaments, fetchEslTournaments, eslTournamentCheckin, eslTournamentMatchCheckin } from '../api/tournaments';

import pubgIcon from '../static/img/DailyGamingTasks/iconPubgDailyTasks.png';
import apexIcon from '../static/img/DailyGamingTasks/iconApexDailyTasks.avif';
import codIcon from '../static/img/DailyGamingTasks/iconCODDailyTasks.avif';
import freeFireIcon from '../static/img/DailyGamingTasks/iconFreeFireDailyTasks.avif';
import MessagePopup from '../components/MessagePopup';
import CannonClashInstructions from '../components/CannonClashInstructions';
import AimTrainInstructions from '../components/AimTrainInstructions';
import popupStyles from '../static/styles/MessagePopup.module.css';
import cannonClashCover from '../static/img/MadeByESL/coverCannonClash.jpeg';
import aimTrainCover from '../static/img/MadeByESL/aimTrainBattle.avif';

// Import static background images
import pubgBg from '../static/img/WeeklyTournaments/imagePubgWeeklyTournament.avif';
import freeFireBg from '../static/img/WeeklyTournaments/imageFreeFireWeeklyTournament.avif';
import { jwtDecode } from 'jwt-decode';

const getGameIcon = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgIcon;
        case 'freefire':
            return freeFireIcon;
        // Add cases for other games like COD, APEX LEGENDS
        default:
            return null; // Or a default icon
    }
};

// Map game names to static background images
const getGameBackground = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgBg;
        case 'freefire':
            return freeFireBg;
        // Add cases for other games
        default:
            return null; // Fallback or default background
    }
};

// Helper function to format date/time
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart, ampm] = dateTimeString.split(' ');
    return {
        date: datePart,
        time: `${timePart} ${ampm}`
    };
};

const WeeklyTournaments = () => {
    const { t } = useTranslation();
    const [tournaments, setTournaments] = useState([]);
    const [eslTournaments, setEslTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRoomPopup, setShowRoomPopup] = useState(false);
    const [roomId, setRoomId] = useState('');
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);
    const [showAimTrainInstructions, setShowAimTrainInstructions] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = `${t('tournaments')} | Jazz Esl`

        // Check if user is logged in
        const accessToken = localStorage.getItem('access_token');
        const msisdn = localStorage.getItem('msisdn');
        setIsLoggedIn(!!accessToken && !!msisdn);

        const loadTournaments = async () => {
            try {
                setLoading(true);
                setError(null);
                const [weeklyData, eslData] = await Promise.all([
                    fetchWeeklyTournaments(),
                    fetchEslTournaments()
                ]);
                setTournaments(weeklyData);
                setEslTournaments(eslData);
            } catch (err) {
                setError(err.message || t('failed_load_tours'));
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, [t]);

    if (loading) {
        return <div className={styles.loading}>{t('loading')}</div>;
    }

    if (error) {
        return <div className={styles.error}>{t('err')}: {error}</div>;
    }

    const handleJoinTournament = (tournamentId) => {
        navigate(`/join-tournament?tournamentId=${tournamentId}`);
    };

    const handleEslTournamentJoin = async (tournament) => {
        if (!isLoggedIn) {
            setPopupMessage(t('login_to_reg'));
            setPopupOpen(true);
            return;
        }

        try {
            if (tournament.is_running === 1) {
                //console.log('Tournament is running, showing room popup');
                setSelectedTournament(tournament);
                setShowRoomPopup(true);
                return;
            }

            const response = await eslTournamentCheckin(tournament.tourn_id);
            //console.log('ESL Tournament Checkin Response:', response);

            if (response.data.already_submited === true) {
                if (tournament.is_running === 1) {
                    //console.log('Already submitted and tournament is running, showing room popup');
                    setSelectedTournament(tournament);
                    setShowRoomPopup(true);
                } else {
                    setPopupMessage(t('already_reg_msg'));
                    setPopupOpen(true);
                }
            } else {
                const isCannonClash = tournament.tournament_game === "cannon_clash_001";
                if (isCannonClash) {
                    setPopupMessage(t('success_join_msg'));
                    setPopupOpen(true);
                    setTimeout(() => {
                        setShowInstructions(true);
                    }, 1500);
                } else {
                    setPopupMessage(t('success_join'));
                    setPopupOpen(true);
                }
            }
        } catch (error) {
            //console.error('ESL Tournament Join Error:', error);
            setPopupMessage(error.message || t('failed_join'));
            setPopupOpen(true);
        }
    };

    const handleRoomIdSubmit = async () => {
        if (!roomId.trim() || !selectedTournament) return;

        try {
            const response = await eslTournamentMatchCheckin(selectedTournament.tourn_id, roomId.trim());
            //console.log('Match Checkin Response:', response);

            // Check for both 'success' and 'successss' (typo in API)
            const isSuccess = response.success === true || response.successss === true;


            if (!isSuccess) {
                // Case 1: Error (too early, invalid room ID, etc.)
                setPopupMessage(response.message || t('failed_checkin'));
                setPopupOpen(true);
            } else {
                // Case 2 & 3: Navigate to game with roomId
                const gameRoute = selectedTournament.tournament_game === "aim_train_001" ? '/aim-train' : '/cannon-clash';
                navigate(gameRoute, {
                    state: {
                        roomId: roomId.trim(),
                        tournamentId: selectedTournament.tourn_id,
                        startGame: response.start_game,
                        tournamentMode: true
                    }
                });
            }
        } catch (error) {
            //console.error('Match Checkin Error:', error);
            setPopupMessage(error.message || t('failed_checkin'));
            setPopupOpen(true);
        } finally {
            setShowRoomPopup(false);
            setRoomId('');
            setSelectedTournament(null);
        }
    };

    const handleRoomPopupClose = () => {
        setShowRoomPopup(false);
        setRoomId('');
        setSelectedTournament(null);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    const handleInstructionsClose = () => {
        setShowInstructions(false);
    };

    const handleAimTrainInstructionsClose = () => {
        setShowAimTrainInstructions(false);
    };

    const handleShowInstructions = (tournament) => {
        if (tournament.tournament_game === "cannon_clash_001") {
            setShowInstructions(true);
        } else if (tournament.tournament_game === "aim_train_001") {
            setShowAimTrainInstructions(true);
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>{t('weekly_tours')}</h2>
            <p className={styles.subheading}>{t('compete_to_win')}</p>

            {eslTournaments.length > 0 && (
                <div className={styles.eslSection}>
                    <div className={styles.sectionSeparator}>
                        <span className={styles.sectionSeparatorText}>{t('esl_tours')}</span>
                    </div>
                    <div className={styles.cardGrid}>
                        {eslTournaments.map((tournament) => (
                            <div key={tournament.Id} className={styles.cardContainer}>
                                <div className={styles.card}>
                                    <div
                                        className={styles.cardContent}
                                        style={{
                                            backgroundImage: tournament.tournament_game === "cannon_clash_001"
                                                ? `url(${cannonClashCover})`
                                                : tournament.tournament_game === "aim_train_001"
                                                    ? `url(${aimTrainCover})`
                                                    : tournament.thumbnail
                                                        ? `url(${tournament.thumbnail})`
                                                        : 'none'
                                        }}
                                    >
                                        {tournament.status === 4 ? (
                                            <div className={styles.completedOverlay}>
                                                <h3>{t('tour_ended')}</h3>
                                            </div>
                                        ) : (
                                            <div className={styles.bottomContent}>
                                                <div className={styles.gameIcon}>
                                                    <img
                                                        src={tournament.thumbnail}
                                                        alt={tournament.tourn_name}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </div>
                                                <div className={styles.timeInfo}>
                                                    <img src={calendarIcon} alt="Calendar" />
                                                    <div>
                                                        <p>{formatDateTime(tournament.time).date}</p>
                                                        <p>{formatDateTime(tournament.time).time}</p>
                                                    </div>
                                                </div>
                                                <div className={styles.rightInfo}>
                                                    {tournament.is_running === 1 && (
                                                        <div className={styles.runningIndicator}>
                                                            <p>🔴 {t('live').toUpperCase()}</p>
                                                        </div>
                                                    )}
                                                    <div className={styles.prizes}>
                                                        {tournament.prize && <p>{tournament.prize} {t('credits')}</p>}
                                                        {tournament.total_checkin && <p>{t('participants')} {tournament.total_checkin}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {tournament.is_running === 1 && (
                                            <button
                                                className={styles.detailsButton}
                                                onClick={() => navigate(`/tournament/${tournament.tourn_id}`)}
                                                title={t('view_t_details')}
                                            >
                                                <span className={styles.detailsIcon}>📊</span>
                                                <span className={styles.detailsText}>{t('details')}</span>
                                            </button>
                                        )}
                                        {(tournament.tournament_game === "cannon_clash_001" || tournament.tournament_game === "aim_train_001") && tournament.status !== 4 && (
                                            <button
                                                className={styles.infoButton}
                                                onClick={() => handleShowInstructions(tournament)}
                                                title={t('view_t_inst')}
                                            >
                                                <span className={styles.infoIcon}>?</span>
                                                <span className={styles.infoText}>{t('how_play')}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className={styles.joinButton}
                                    onClick={() => handleEslTournamentJoin(tournament)}
                                    style={{ display: tournament.status === 4 ? 'none' : 'block' }}
                                >
                                    {!isLoggedIn ? t('login_req') : (tournament.is_running === 1 ? t('play_now') : t('join_now'))}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {eslTournaments.length > 0 && tournaments.length > 0 && (
                <div className={styles.sectionSeparator}>
                    <span className={styles.sectionSeparatorText}>{t('weekly_tours')}</span>
                </div>
            )}

            <div className={styles.cardGrid}>
                {tournaments.map((tournament) => {
                    const { date, time } = formatDateTime(tournament.time);
                    const isCompleted = tournament.status !== 1 || tournament.is_running === 1;
                    const gameIconSrc = getGameIcon(tournament.tourn_name);
                    const gameBgSrc = getGameBackground(tournament.tourn_name);

                    return (
                        <div key={tournament.id} className={styles.cardContainer}>
                            <div className={styles.card}>
                                <div
                                    className={styles.cardContent}
                                    style={{ backgroundImage: gameBgSrc ? `url(${gameBgSrc})` : 'none' }}
                                >
                                    {isCompleted ? (
                                        <div className={styles.completedOverlay}>
                                            <h3>{t('tour_ended')}</h3>
                                            <p>{t('view_t_details')}</p>
                                        </div>
                                    ) : (
                                        <div className={styles.bottomContent}>
                                            <div className={styles.gameIcon}>
                                                {gameIconSrc && <img src={gameIconSrc} alt={tournament.tourn_name} />}
                                            </div>
                                            <div className={styles.timeInfo}>
                                                <img src={calendarIcon} alt="Calendar" />
                                                <div>
                                                    <p>{t('starting_at')}</p>
                                                    <p>{date}</p>
                                                    <p>{time}</p>
                                                </div>
                                            </div>
                                            <div className={styles.rightInfo}>
                                                <h3 className={styles.entryFee}>
                                                    {
                                                        tournament.entryFee != null ?
                                                            `${tournament.entryFee} ${t('credits')}` : // If entryFee has a value
                                                            (tournament.isEntryFree === false ? 'N/A' : t('free_entry')) // If entryFee is null, check isEntryFree. False means N/A, otherwise (true or null) it's Free.
                                                    }
                                                </h3>
                                                <div className={styles.prizes}>
                                                    {tournament.prize && <p>{t('1st_creds', { credits: tournament.prize })}</p>}
                                                    {tournament.second_prize && <p>{t('2nd_creds', { credits: tournament.second_prize })}</p>}
                                                    {tournament.third_prize && <p>{t('3rd_creds', { credits: tournament.third_prize })}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                className={styles.joinButton}
                                onClick={() => handleJoinTournament(tournament.tourn_id)}
                            >
                                {t('join_now')}
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Room ID Popup */}
            {showRoomPopup && (
                <div className={popupStyles.popupOverlay}>
                    <div className={popupStyles.popupContent}>
                        <h3>{t('enter_room_id')}</h3>
                        <p>{t('already_checked')}</p>
                        <input
                            type="text"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            placeholder={t('enter_room_id')}
                            className={popupStyles.roomInput}
                        />
                        <div className={popupStyles.popupButtons}>
                            <button onClick={handleRoomIdSubmit} className={popupStyles.submitButton}>
                                Submit
                            </button>
                            <button onClick={handleRoomPopupClose} className={popupStyles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MessagePopup
                isOpen={popupOpen}
                message={popupMessage}
                onClose={handlePopupClose}
            />

            <CannonClashInstructions
                isOpen={showInstructions}
                onClose={handleInstructionsClose}
            />

            <AimTrainInstructions
                isOpen={showAimTrainInstructions}
                onClose={handleAimTrainInstructionsClose}
            />
        </div>
    );
};

export default WeeklyTournaments; 